import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/* 세션은 httpOnly 쿠키에 있어 자바스크립트가 읽을 수 없다.
   따라서 로그인 여부는 /api/members/me 로 서버에 물어본다. */

const AuthContext = createContext(null);

async function call(path, options = {}) {
  const res = await fetch(`/api/members${path}`, {
    credentials: "same-origin",
    headers: options.body ? { "Content-Type": "application/json" } : undefined,
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string" ? data.detail : "요청을 처리하지 못했습니다."
    );
  }
  return data;
}

export function AuthProvider({ children }) {
  const [member, setMember] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await call("/me");
      setMember(data.member || null);
    } catch {
      setMember(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({
    member,
    ready,
    refresh,
    async login(email, password) {
      const data = await call("/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      setMember(data.member);
      return data.member;
    },
    async signup(payload) {
      const data = await call("/signup", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (data.member) setMember(data.member);
      return data;
    },
    async logout() {
      await call("/logout", { method: "POST" });
      setMember(null);
    },
    async updateProfile(payload) {
      const data = await call("/me", { method: "POST", body: JSON.stringify(payload) });
      setMember(data.member);
      return data.member;
    },
    call,
  }), [member, ready, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 는 AuthProvider 안에서만 쓸 수 있습니다.");
  return ctx;
}
